import React, { useEffect, useState } from "react";
import {
Form,
Input,
Select,
Row,
Col,
Button,
Drawer,
DatePicker,
Upload,
message,
Spin,
} from "antd";
import { LeftOutlined, InboxOutlined, CloseOutlined } from "@ant-design/icons"; // Import CloseOutlined
import { useDispatch, useSelector } from "react-redux";
import {
getAdminCountryList,
getPartnersProductList,
raiseSalesRequest,
getSingleSalesRequestData,
editRoyaltySalesRequestData,
deleteSalesRequestData,
} from "../../appRedux/actions";
import { royalty_countries_product } from "../../constants";
import { useGetPartnersProductListQuery } from "../../appRedux/api/royaltySalesApi";
import moment from "moment";
import { getUserDetails } from "../../helpers";
import "./style.css";

const { TextArea } = Input;
const { Dragger } = Upload;
const { Option } = Select;

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 5 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 18 },
    },
};

const PartnerRaiseEditApprovalRequestDrawer = ({ 
    visible, 
    onClose, 
    editMode, 
    requestId,
    onSuccess
    }) => {
    const { editSalesRequestData } = useSelector(
        ({ royaltySales }) => royaltySales
    );
    const [editFlag, setEditFlag] = useState(false);
    const [products, setProducts] = useState([]);
    const [editedFields, setEditedFields] = useState({});
    const [removedFiles, setRemovedFiles] = useState([]);
    const [initialFiles, setInitialFiles] = useState([]);
    const [userDetails, setUserDetails] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch products from API - includes ALL products (FDF, API, and Diagnostics)
    const { data: apiProducts, isLoading: productsLoading } = useGetPartnersProductListQuery(undefined, {
        skip: !visible,
    });

    useEffect(() => {
        setUserDetails(getUserDetails());
    }, []);

    const handleFieldChange = (changedValues) => {
        setEditedFields((prevEditedFields) => ({
        ...prevEditedFields,
        ...changedValues,
        }));
    };

    const dispatch = useDispatch();
    const [form] = Form.useForm();

    useEffect(() => {
        if (editMode && requestId && visible) {
        dispatch(getSingleSalesRequestData(requestId));
        setEditFlag(true);
        } else if (!editMode) {
        // Reset form and state when switching to add mode
        form.resetFields();
        setEditedFields({});
        setRemovedFiles([]);
        setInitialFiles([]);
        setProducts([]);
        setEditFlag(false);
        }
    }, [editMode, requestId, visible, dispatch, form]);

    useEffect(() => {
        if (editMode && editSalesRequestData) {
        const initialFiles = editSalesRequestData?.file
            ?.filter((file) => file.category === 'PR/PO')
            .map((file) => ({
            uid: file.po_id,
            name: file.po_document.split("/").pop(),
            status: "done",
            url: file.po_document,
            })) || [];
        
        setInitialFiles(initialFiles);
        
        form.setFieldsValue({
            country_id: editSalesRequestData?.country_id?.country_id,
            product_id: editSalesRequestData?.product_id?.product_id,
            expected_date_of_sale: moment(
            editSalesRequestData?.expected_date_of_sale
            ),
            pack_size: editSalesRequestData?.pack_size,
            quantity: editSalesRequestData?.quantity,
            buyer_name_and_address: editSalesRequestData?.buyer_name_and_address,
            end_customer_and_address: editSalesRequestData?.end_customer_and_address,
            proof_of_sale_details: editSalesRequestData?.proof_of_sale_details,
            file: initialFiles,
        });

        if (editSalesRequestData?.country_id?.country_id) {
            const selectedCountry = royalty_countries_product.find(
            (country) => country.country_id === editSalesRequestData.country_id.country_id
            );
            setProducts(selectedCountry ? selectedCountry.product : []);
        }
        }
    }, [editMode, editSalesRequestData, form]);

    const handleCountryChange = (value) => {
        const selectedCountry = royalty_countries_product.find(
        (country) => country.country_id === value
        );
        setProducts(selectedCountry ? selectedCountry.product : []);
        form.setFieldsValue({ product_id: undefined });
    };

const getProduct = (value) => {
        const selectedCountry = royalty_countries_product.find(
        (country) => country.country_id === value
        );
        return selectedCountry ? selectedCountry.product : products;
    };

    // Handle product selection
    const handleProductChange = () => {};

    // Get display products - use API products if available, otherwise fallback to static list
    const displayProducts = apiProducts && apiProducts.length > 0 ? apiProducts : getProduct();

    const handleRemove = (file) => {
        if (file.url) {
        setRemovedFiles(prev => [...prev, file.uid]);
        }
        return true;
    };

    const normFile = (e) => {
        if (Array.isArray(e)) {
        return e;
        }
        return e && e.fileList;
    };

    const checkFile = (file) => {
        const allowedFileTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
        const isTypeValid = allowedFileTypes.includes(file.type);
        const isSizeValid = file.size / 1024 / 1024 <= 15;

        if (!isTypeValid) {
        message.error("Please upload a PDF, JPEG, or PNG document!");
        } else if (!isSizeValid) {
        message.error("File size must be less than or equal to 15 MB!");
        }

        return isTypeValid && isSizeValid;
    };

const onFinish = async (values) => {
    setIsSubmitting(true);
    const formData = new FormData();
    
    if (!editMode) {
        values.file.map((data) => {
            formData.append("file", data.originFileObj);
        });
        formData.append("proof_of_sale_details", values.proof_of_sale_details);
        // Fix the date format - use moment's format instead of toISOString()
        formData.append(
            "expected_date_of_sale",
            values.expected_date_of_sale.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
        );
        formData.append("pack_size", values.pack_size);
        formData.append("quantity", values.quantity);
        formData.append("buyer_name_and_address", values.buyer_name_and_address);
        formData.append(
            "end_customer_and_address",
            values.end_customer_and_address
        );
        formData.append("product_id", values.product_id);
        formData.append("country_id", values.country_id);
        dispatch(raiseSalesRequest(formData, () => {
            setIsSubmitting(false);
            onClose();
            if (onSuccess) {
                onSuccess();
            }
        }));
        } else {
        if (removedFiles.length > 0) {
            try {
            for (const fileId of removedFiles) {
                await dispatch(deleteSalesRequestData(fileId));
            }
            } catch (error) {
            message.error("Failed to delete some files");
            setIsSubmitting(false);
            return;
            }
        }
        
        Object.entries(editedFields).forEach(([key, value]) => {
            if (Array.isArray(value)) {
            const filesWithOriginFileObj = value.filter(
                (file) => file.originFileObj
            );
            filesWithOriginFileObj.forEach((file, index) => {
                formData.append(`${key}`, file.originFileObj);
            });
            } else {
            // Handle date field specifically in edit mode
            if (key === 'expected_date_of_sale' && moment.isMoment(value)) {
                formData.append(key, value.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"));
            } else {
                formData.append(key, value);
            }
            }
        });
        formData.append('status', 'PENDING');
        dispatch(editRoyaltySalesRequestData(requestId, formData, () => {
            setIsSubmitting(false);
            onClose();
            if (onSuccess) {
                onSuccess();
            }
        }));
    }
};

const handleClose = () => {
        onClose();
        // Reset state after signalling parent to close
        form.resetFields();
        setRemovedFiles([]);
        setInitialFiles([]);
        setEditedFields({});
        setIsSubmitting(false);
    };

    return (
        <Drawer
        className="partner-raise-edit-approval-request-drawer"
        title={null} // Remove the default title
        placement="right"
        width={1000}
        onClose={isSubmitting ? () => {} : handleClose}
        open={visible}
        closable={false} // Disable the default close button
        footer={
            <div className="drawer-footer" style={{ textAlign: 'right' }}>
            <Button onClick={handleClose} style={{ marginRight: 8 }} disabled={isSubmitting}>
                Cancel
            </Button>
            <Button 
                type="primary" 
                onClick={() => form.submit()} 
                disabled={isSubmitting}
            >
                {editMode ? 'Save' : 'Submit Request'}
            </Button>
            </div>
        }
        >
        {/* Loading overlay */}
        {isSubmitting && (
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <Spin size="large" />
            </div>
        )}
        
        {/* Custom title inside the drawer body */}
        <div className="drawer-header">
            <span>{editMode ? "Edit Request" : "Add Request"}</span>
            <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClose}
                disabled={isSubmitting}
            />
        </div>

        <Form
            {...formItemLayout}
            form={form}
            name="add-request"
            onValuesChange={handleFieldChange}
            onFinish={onFinish}
            disabled={isSubmitting}
            initialValues={{}}
            scrollToFirstError
        >
            <Form.Item
            name="country_id"
            label="Country"
            rules={[
                { required: true, message: "Please select a country" },
            ]}
            >
            <Select onChange={handleCountryChange}>
                {royalty_countries_product
                .filter(country => !(userDetails === "Emcure" && country.country_name === "India"))
                .map((country) => (
                <Option
                    key={country.country_id}
                    value={country.country_id}
                >
                    {country.country_name}
                </Option>
                ))}
            </Select>
            </Form.Item>

<Form.Item
            name="product_id"
            label="Product"
            rules={[
                { required: true, message: "Please select a product" },
            ]}
            >
            <Select
                onChange={handleProductChange}
                loading={productsLoading}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                    const label = option?.label ?? option?.children ?? option?.value ?? "";
                    return String(label).toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
            >
                {displayProducts.map((product) => (
                <Option
                    key={product.product_id}
                    value={product.product_id}
                >
                    {product.product_name} {product.category ? `(${product.category})` : ''}
                </Option>
                ))}
            </Select>
            </Form.Item>

            <Form.Item
            label="Expected date of sale"
            name="expected_date_of_sale"
            rules={[
                { required: true, message: "Please select a date!" },
            ]}
            >
            <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
            label="Pack Size"
            name="pack_size"
            rules={[
                {
                required: true,
                message: "Please enter the pack size!",
                },
                ({ getFieldValue }) => ({
                validator(_, value) {
                    const inputValue = parseInt(value, 10);
                    if (
                    !isNaN(inputValue) &&
                    inputValue >= 0 &&
                    inputValue <= 10000
                    ) {
                    return Promise.resolve();
                    }
                    return Promise.reject(
                    new Error("Pack size must be between 0 and 10000")
                    );
                },
                }),
            ]}
            >
            <Input type="number" />
            </Form.Item>

            <Form.Item
            label="Quantity"
            name="quantity"
            rules={[
                {
                required: true,
                message: "Please enter the pack size!",
                },
                ({ getFieldValue }) => ({
                validator(_, value) {
                    const inputValue = parseInt(value, 10);
                    if (
                    !isNaN(inputValue) &&
                    inputValue >= 0 &&
                    inputValue <= 99000000
                    ) {
                    return Promise.resolve();
                    }
                    return Promise.reject(
                    new Error("Pack size must be between 0 and 99Mn")
                    );
                },
                }),
            ]}
            >
            <Input name="quantity" type="number" />
            </Form.Item>

            <Form.Item
            label="Buyer Name & Address"
            name="buyer_name_and_address"
            >
            <TextArea rows={4} />
            </Form.Item>

            <Form.Item
            label="End Customer & Address"
            name="end_customer_and_address"
            >
            <TextArea rows={4} />
            </Form.Item>
            <Form.Item
            label="Proof of sales details"
            name="proof_of_sale_details"
            >
            <TextArea rows={4} />
            </Form.Item>
            <Form.Item
            label="Proof of sale (PR/PO)"
            name="file"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            rules={[
                {required: !editMode, message: "" },
                ({ getFieldValue, getFieldError }) => ({
                validator(_, value) {
                    const fileList = getFieldValue("file");
                    const isTouched =
                    !!getFieldError("file") ||
                    fileList.some(
                        (file) =>
                        file.status === "done" ||
                        file.status === "uploading"
                    );

                    if (!isTouched) {
                    return Promise.resolve();
                    }

                    const allowedFileTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
                    const isFileTypeValid = fileList.every(
                    (file) =>
                        !file.originFileObj ||
                        allowedFileTypes.includes(file.type)
                    );

                    if (!isFileTypeValid) {
                    return Promise.reject(
                        new Error("Please upload a PDF, JPEG, or PNG document!")
                    );
                    }

                    const isSizeValid = fileList.every(
                    (file) =>
                        !file.originFileObj ||
                        (allowedFileTypes.includes(file.type) &&
                        file.size / 1024 / 1024 <= 15)
                    );

                    if (!isSizeValid) {
                    return Promise.reject(
                        new Error(
                        "File size must be less than or equal to 15 MB!"
                        )
                    );
                    }

                    return Promise.resolve();
                },
                }),
            ]}
            >
            <Dragger
                name="file"
                multiple={true}
                beforeUpload={checkFile}
                accept=".pdf, .png, .jpeg, .jpg"
                onRemove={handleRemove}
                customRequest={({ file, onSuccess }) => {
                onSuccess();
                }}
            >
                <p className="ant-upload-drag-icon">
                <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                Click or drag files to this area to upload
                </p>
                <p className="ant-upload-hint">
                Support for a single or bulk upload (pdf, png, jpeg).
                </p>
            </Dragger>
            </Form.Item>
        </Form>
        </Drawer>
    );
};

export default PartnerRaiseEditApprovalRequestDrawer;